<!-- 商家端 - 店铺设置 -->
<template>
  <ContentWrap title="店铺信息">
    <template #header>
      <el-button v-if="!editing" type="primary" @click="editing = true">
        <Icon icon="ep:edit" /> 编辑
      </el-button>
      <template v-else>
        <el-button @click="cancelEdit">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveShop">保存</el-button>
      </template>
    </template>

    <el-form v-if="editing" :model="editForm" label-width="100px">
      <el-form-item label="店铺名称">
        <el-input v-model="editForm.shopName" maxlength="100" placeholder="请输入店铺名称" />
      </el-form-item>
      <el-form-item label="店铺Logo">
        <UploadImg v-model="editForm.shopLogo" width="120px" height="120px" directory="shop/logo" />
        <div class="form-tip">C 端店铺头像</div>
      </el-form-item>
      <el-form-item label="店铺头图">
        <UploadImg
          v-model="editForm.shopBanner"
          width="240px"
          height="100px"
          directory="shop/banner"
        />
        <div class="form-tip">C 端顶部背景，建议宽图约 750×300</div>
      </el-form-item>
      <el-form-item label="店铺简介">
        <el-input
          v-model="editForm.shopDesc"
          type="textarea"
          :rows="3"
          maxlength="500"
          show-word-limit
          placeholder="请输入店铺简介"
        />
      </el-form-item>
      <el-form-item label="联系电话">
        <el-input v-model="editForm.contactPhone" maxlength="20" placeholder="请输入联系电话" />
      </el-form-item>
      <el-divider content-position="left">退货地址（买家退货必填）</el-divider>
      <el-form-item label="收件人" required>
        <el-input v-model="editForm.returnName" maxlength="64" placeholder="退货收件人" />
      </el-form-item>
      <el-form-item label="收件手机" required>
        <el-input v-model="editForm.returnMobile" maxlength="20" placeholder="退货收件手机" />
      </el-form-item>
      <el-form-item label="所在地区" required>
        <el-cascader
          v-model="editForm.returnAreaId"
          :options="areaList"
          :props="areaProps"
          clearable
          placeholder="请选择省市区"
          class="!w-full"
        />
      </el-form-item>
      <el-form-item label="详细地址" required>
        <el-input
          v-model="editForm.returnDetailAddress"
          maxlength="255"
          placeholder="街道门牌等详细地址"
        />
      </el-form-item>
      <el-divider content-position="left">团购司机取货坐标（选填）</el-divider>
      <el-form-item label="取货经度">
        <el-input-number v-model="editForm.longitude" :precision="6" class="!w-full" placeholder="如 118.79" />
      </el-form-item>
      <el-form-item label="取货纬度">
        <el-input-number v-model="editForm.latitude" :precision="6" class="!w-full" placeholder="如 32.06" />
      </el-form-item>
    </el-form>

    <el-descriptions v-else :column="2" border>
      <el-descriptions-item label="店铺名称" min-width="150">{{ shopInfo.shopName }}</el-descriptions-item>
      <el-descriptions-item label="店铺状态">
        <el-tag :type="shopStatusInfo.type">{{ shopStatusInfo.label }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="店铺 Logo">
        <el-image
          v-if="shopInfo.shopLogo"
          :src="shopInfo.shopLogo"
          class="h-60px w-60px"
          fit="cover"
        />
        <span v-else class="text-gray-400">未设置</span>
      </el-descriptions-item>
      <el-descriptions-item label="店铺头图">
        <el-image
          v-if="shopInfo.shopBanner"
          :src="shopInfo.shopBanner"
          class="h-60px w-140px"
          fit="cover"
        />
        <span v-else class="text-gray-400">未设置（C 端用渐变兜底）</span>
      </el-descriptions-item>
      <el-descriptions-item label="店铺简介">{{
        shopInfo.shopDesc || shopInfo.description || '暂无简介'
      }}</el-descriptions-item>
      <el-descriptions-item label="联系电话">{{ shopInfo.contactPhone || '-' }}</el-descriptions-item>
      <el-descriptions-item label="退货收件人">{{ shopInfo.returnName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="退货手机">{{ shopInfo.returnMobile || '-' }}</el-descriptions-item>
      <el-descriptions-item label="退货地址" :span="2">
        {{ formatReturnAddress(shopInfo) }}
      </el-descriptions-item>
      <el-descriptions-item label="取货经度">{{ shopInfo.longitude ?? '-' }}</el-descriptions-item>
      <el-descriptions-item label="取货纬度">{{ shopInfo.latitude ?? '-' }}</el-descriptions-item>
      <el-descriptions-item label="创建时间">{{ formatDate(shopInfo.createTime) }}</el-descriptions-item>
    </el-descriptions>
  </ContentWrap>

  <ContentWrap title="经营数据" class="mt-16px">
    <el-row :gutter="20">
      <el-col :span="6">
        <el-statistic title="在售商品数" :value="businessData.onSaleProductCount" />
      </el-col>
      <el-col :span="6">
        <el-statistic title="累计订单数" :value="businessData.orderCount" />
      </el-col>
      <el-col :span="6">
        <el-statistic title="店铺评分" :value="shopInfo.score || 0" :precision="1" suffix="分" />
      </el-col>
      <el-col :span="6">
        <div class="stat-item">
          <div class="stat-title">入驻时间</div>
          <div class="stat-value">{{ shopInfo.createTime ? formatDate(shopInfo.createTime, 'YYYY-MM-DD') : '--' }}</div>
        </div>
      </el-col>
    </el-row>
  </ContentWrap>

  <ContentWrap title="入驻套餐" class="mt-16px">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="当前套餐">{{ planInfo.planName || (planInfo.planId ? '套餐#' + planInfo.planId : '未绑定（不限）') }}</el-descriptions-item>
      <el-descriptions-item label="等级序">{{ planInfo.planLevel ?? '-' }}</el-descriptions-item>
      <el-descriptions-item label="买入价">
        ¥{{ ((planInfo.planPrice || 0) / 100).toFixed(2) }}
      </el-descriptions-item>
      <el-descriptions-item label="商品额度">
        {{ planInfo.usedSpuCount ?? 0 }} /
        {{ planInfo.planSpuLimit == null || planInfo.planSpuLimit < 0 ? '不限' : planInfo.planSpuLimit }}
      </el-descriptions-item>
    </el-descriptions>
    <div class="mt-12px">
      <el-button type="primary" plain @click="openUpgrade">升档</el-button>
    </div>
  </ContentWrap>

  <Dialog v-model="upgradeVisible" title="自助升档" width="520px">
    <template v-if="!pendingPayOrderId">
      <el-radio-group v-model="upgradePlanId" class="upgrade-plans">
        <el-radio
          v-for="p in upgradeOptions"
          :key="p.id"
          :value="p.id"
          :disabled="(p.level || 0) <= (planInfo.planLevel || 0)"
          border
          class="!mb-8px !mr-0 !w-full"
        >
          {{ p.name }} · ¥{{ ((p.price || 0) / 100).toFixed(2) }} ·
          上限 {{ p.spuLimit == null || p.spuLimit < 0 ? '不限' : p.spuLimit }}
        </el-radio>
      </el-radio-group>
    </template>
    <el-alert
      v-else
      type="warning"
      :closable="false"
      :title="`待支付差价 ¥${((pendingPayAmount || 0) / 100).toFixed(2)}（支付单 #${pendingPayOrderId}）`"
      description="请完成支付后点击「同步支付状态」；差价为 0 时会立即升档。"
    />
    <template #footer>
      <el-button @click="closeUpgrade">取消</el-button>
      <el-button
        v-if="pendingPayOrderId"
        type="primary"
        :loading="upgrading"
        @click="syncUpgradePay"
      >
        同步支付状态
      </el-button>
      <el-button v-else type="primary" :loading="upgrading" @click="submitUpgrade">确认升档</el-button>
    </template>
  </Dialog>

  <ContentWrap title="经营类目" class="mt-16px">
    <el-table :data="categoryList" stripe row-key="id" default-expand-all>
      <el-table-column label="类目名称" min-width="220">
        <template #default="{ row }">
          <div class="category-name" :style="{ paddingLeft: (row.level || 0) * 24 + 'px' }">
            <Icon v-if="row.level" icon="ep:arrow-right" class="level-icon" />
            <span class="font-medium">{{ row.categoryPath || row.categoryName }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="类目层级" width="120">
        <template #default="{ row }">
          <el-tag size="small" :type="row.level === 0 ? 'primary' : 'info'">{{ row.level === 0 ? '一级类目' : '二级类目' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="佣金率" width="150">
        <template #default="{ row }">{{ row.commissionRate || 0 }}%</template>
      </el-table-column>
    </el-table>
  </ContentWrap>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { ContentWrap } from '@/components/ContentWrap'
import { UploadImg } from '@/components/UploadFile'
import { Dialog } from '@/components/Dialog'
import { formatDate } from '@/utils/formatTime'
import { defaultProps } from '@/utils/tree'
import { getAreaTree } from '@/api/merchant/system/area'
import { formatReturnAddressLine } from '@/utils/areaFormat'
import * as MerchantShopApi from '@/api/merchant/shop'
import * as DashboardApi from '@/api/merchant/dashboard'
import { patchMerchantShopName } from '@/views/merchant/registry/merchant-session'

defineOptions({ name: 'MerchantShop' })

const shopInfo = reactive<any>({})
const categoryList = ref<any[]>([])
const editing = ref(false)
const saving = ref(false)
const areaList = ref<any[]>([])
const areaProps = { ...defaultProps, emitPath: false }
const businessData = reactive({
  onSaleProductCount: 0,
  orderCount: 0
})
const planInfo = reactive<any>({
  planId: null,
  planName: '',
  planLevel: null,
  planPrice: 0,
  planSpuLimit: null,
  usedSpuCount: 0
})
const upgradeVisible = ref(false)
const upgradeOptions = ref<any[]>([])
const upgradePlanId = ref<number>()
const upgrading = ref(false)
const pendingPayOrderId = ref<number>()
const pendingPayAmount = ref(0)
const editForm = reactive({
  shopName: '',
  shopLogo: '',
  shopBanner: '',
  shopDesc: '',
  contactPhone: '',
  returnName: '',
  returnMobile: '',
  returnAreaId: undefined as number | undefined,
  returnDetailAddress: '',
  longitude: undefined as number | undefined,
  latitude: undefined as number | undefined
})

function fillEditFormFromShop() {
  editForm.shopName = shopInfo.shopName || ''
  editForm.shopLogo = shopInfo.shopLogo || ''
  editForm.shopBanner = shopInfo.shopBanner || ''
  editForm.shopDesc = shopInfo.shopDesc || shopInfo.description || ''
  editForm.contactPhone = shopInfo.contactPhone || ''
  editForm.returnName = shopInfo.returnName || ''
  editForm.returnMobile = shopInfo.returnMobile || ''
  editForm.returnAreaId = shopInfo.returnAreaId ?? undefined
  editForm.returnDetailAddress = shopInfo.returnDetailAddress || ''
  editForm.longitude = shopInfo.longitude ?? undefined
  editForm.latitude = shopInfo.latitude ?? undefined
}

function formatReturnAddress(info: any) {
  return formatReturnAddressLine(areaList.value, info) || '未配置'
}

const shopStatusMap: Record<number, { label: string; type: any }> = {
  0: { label: '未激活', type: 'info' },
  1: { label: '正常营业', type: 'success' },
  2: { label: '已冻结', type: 'danger' },
  3: { label: '关店中', type: 'warning' },
  4: { label: '已关闭', type: 'info' }
}
const shopStatusInfo = computed(() => {
  return shopStatusMap[shopInfo.shopStatus] ?? { label: '状态未知', type: 'info' }
})

const getShopInfo = async () => {
  const data = await MerchantShopApi.getShopInfo()
  Object.assign(shopInfo, data)
}

const getPlanInfo = async () => {
  try {
    const used = shopInfo.productCount ?? businessData.onSaleProductCount
    const data = await MerchantShopApi.getCurrentPlan(used)
    Object.assign(planInfo, data || {})
    if (shopInfo.planId && !planInfo.planId) {
      planInfo.planId = shopInfo.planId
      planInfo.planPrice = shopInfo.planPrice
      planInfo.planSpuLimit = shopInfo.planSpuLimit
    }
    if (planInfo.usedSpuCount == null && used != null) {
      planInfo.usedSpuCount = used
    }
  } catch (e) {
    console.warn('获取套餐信息失败:', e)
  }
}

const closeUpgrade = () => {
  upgradeVisible.value = false
  pendingPayOrderId.value = undefined
  pendingPayAmount.value = 0
}

const openUpgrade = async () => {
  upgradeOptions.value = (await MerchantShopApi.getUpgradeOptions()) || []
  upgradePlanId.value = undefined
  pendingPayOrderId.value = undefined
  pendingPayAmount.value = 0
  upgradeVisible.value = true
}

const submitUpgrade = async () => {
  if (!upgradePlanId.value) {
    ElMessage.warning('请选择目标套餐')
    return
  }
  upgrading.value = true
  try {
    const data = await MerchantShopApi.upgradePlan(upgradePlanId.value)
    if (data?.upgraded) {
      ElMessage.success('升档成功')
      closeUpgrade()
      await getPlanInfo()
      return
    }
    if (data?.payOrderId) {
      pendingPayOrderId.value = data.payOrderId
      pendingPayAmount.value = data.amount || 0
      ElMessage.info('请完成差价支付后点「同步支付状态」')
    }
  } finally {
    upgrading.value = false
  }
}

const syncUpgradePay = async () => {
  if (!pendingPayOrderId.value) return
  upgrading.value = true
  try {
    await MerchantShopApi.syncPlanPay(pendingPayOrderId.value)
    ElMessage.success('升档成功')
    closeUpgrade()
    await getPlanInfo()
  } catch {
    ElMessage.warning('尚未支付成功，请支付后再同步')
  } finally {
    upgrading.value = false
  }
}

const getShopCategories = async () => {
  const data = await MerchantShopApi.getShopCategories()
  categoryList.value = (data || []).map((item: any) => ({
    ...item,
    level: item.level ?? (item.parentCategoryName ? 1 : 0)
  }))
}

const getBusinessData = async () => {
  try {
    const [onSaleRes, orderRes] = await Promise.all([
      DashboardApi.getOnSaleProductCount(),
      DashboardApi.getOrderCount?.() ?? DashboardApi.getTodayOrderCount()
    ])
    businessData.onSaleProductCount = onSaleRes?.total || 0
    businessData.orderCount = orderRes?.total || 0
  } catch (e) {
    console.warn('获取经营数据失败:', e)
  }
}

function cancelEdit() {
  editing.value = false
  fillEditFormFromShop()
}

watch(editing, (val) => {
  if (val) {
    fillEditFormFromShop()
  }
})

async function saveShop() {
  if (!editForm.shopName.trim()) {
    ElMessage.warning('请输入店铺名称')
    return
  }
  if (
    !editForm.returnName?.trim() ||
    !editForm.returnMobile?.trim() ||
    editForm.returnAreaId == null ||
    !editForm.returnDetailAddress?.trim()
  ) {
    ElMessage.warning('请完善退货地址（收件人、手机、地区、详细地址）')
    return
  }
  if (!/^1[3-9]\d{9}$/.test(editForm.returnMobile.trim())) {
    ElMessage.warning('请输入正确的退货收件手机号')
    return
  }
  saving.value = true
  try {
    await MerchantShopApi.updateShop(editForm)
    ElMessage.success('保存成功')
    editing.value = false
    await getShopInfo()
    if (editForm.shopName.trim()) {
      patchMerchantShopName(editForm.shopName.trim())
    }
  } catch (e) {
    // error handled by axios interceptor
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    // 商家中心必须走 merchant-api；误用 admin-api 地区树会鉴权失败 → 级联「暂无数据」
    areaList.value = (await getAreaTree()) || []
  } catch (e) {
    console.warn('加载地区树失败', e)
    areaList.value = []
  }
  await getShopInfo()
  await getShopCategories()
  await getBusinessData()
  await getPlanInfo()
})
</script>

<style scoped>
.mt-10px {
  margin-top: 10px;
}
.ml-10px {
  margin-left: 10px;
}

.category-name {
  display: flex;
  align-items: center;
  gap: 6px;

  .level-icon {
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }

  .font-medium {
    font-weight: 500;
  }
}

.stat-item {
  .stat-title {
    font-size: 13px;
    color: var(--el-text-color-secondary);
    margin-bottom: 8px;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: var(--el-text-color-primary);
    line-height: 1.2;
  }
}

.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
}
</style>
